import { Fancybox } from "@fancyapps/ui/src/Fancybox/Fancybox.js"
import { Ref, ref } from "vue"

export const isFancyboxOpen: Ref<boolean> = ref(false)

let hasHistoryEntry = false

function closeOnBrowserBack(event: PopStateEvent) {
  event.stopImmediatePropagation()
  hasHistoryEntry = false
  window.removeEventListener("popstate", closeOnBrowserBack, true)
  Fancybox.close()
}

/**
 * Gives the preview one same-URL history entry so browser Back closes it without
 * navigating away from the current Item.
 */
const historyEvents = {
  init: () => {
    isFancyboxOpen.value = true
    window.history.pushState(window.history.state, "", window.location.href)
    hasHistoryEntry = true
    window.addEventListener("popstate", closeOnBrowserBack, true)
  },
  closing: () => {
    isFancyboxOpen.value = false
    if (hasHistoryEntry) window.history.back()
  },
  destroy: () => {
    isFancyboxOpen.value = false
  }
}

Fancybox.defaults.on = {
  ...Fancybox.defaults.on,
  ...historyEvents
}

export const bindFancyBox = ({
  id,
  options,
  enableHash
}: {
  id: string
  options
  enableHash?: boolean
}) => {
  Fancybox.bind(id, {
    ...options,
    on: {
      ...options.on,
      init: historyEvents.init,
      destroy: historyEvents.destroy,
      closing: (...args) => {
        historyEvents.closing()
        options.on?.closing?.(...args)
      }
    }
  })
  Fancybox.defaults.Hash = !!enableHash
  return Fancybox
}

const ignoreInWindow = () => undefined

/**
 * Opens the image gallery for a click inside another window (the AI chat PiP): Fancybox.bind only hears
 * clicks in this document, and its `instanceof Element` checks reject elements from another window.
 * Fancybox still listens on this window, so everything that would act on it is turned off: history and
 * video pausing, hash, scroll lock, scrollbar hiding, focus handling, keyboard, and mouse dragging.
 * The fullscreen button is blanked because a PiP window cannot enter fullscreen; the list keeps its
 * length since Fancybox merges option arrays by index, and the toolbar skips unknown entries.
 * Returns the gallery so the caller can remove it together with the window.
 */
export const openFancyboxInWindow = (event: MouseEvent, parentEl: HTMLElement) => {
  if (event.defaultPrevented || event.button || event.ctrlKey || event.metaKey || event.shiftKey) {
    return null
  }
  const target = (event.target as Element | null)?.closest<HTMLElement>("[data-fancybox]")
  if (!target) return null
  event.preventDefault()
  const group = target.dataset.fancybox
  const triggers = group
    ? Array.from(
        target.ownerDocument.querySelectorAll<HTMLElement>(`[data-fancybox="${CSS.escape(group)}"]`)
      )
    : [target]
  const openerOptions = Fancybox.openers.get("[data-fancybox]")
  return Fancybox.show(
    triggers.map(trigger => ({ $trigger: trigger, caption: trigger.dataset.caption })),
    {
      ...openerOptions,
      Toolbar: {
        ...openerOptions?.Toolbar,
        display: openerOptions?.Toolbar?.display?.map(button =>
          button === "fullscreen" ? "" : button
        )
      },
      Image: {
        ...openerOptions?.Image,
        Panzoom: { ...openerOptions?.Image?.Panzoom, touch: false }
      },
      Carousel: { Panzoom: { touch: false } },
      on: {
        init: ignoreInWindow,
        closing: ignoreInWindow,
        destroy: ignoreInWindow,
        "Carousel.change": ignoreInWindow
      },
      Hash: false,
      ScrollLock: false,
      hideScrollbar: false,
      trapFocus: false,
      autoFocus: false,
      keyboard: false,
      dragToClose: false,
      startIndex: triggers.indexOf(target),
      parentEl
    }
  )
}

/** Removes a gallery opened by openFancyboxInWindow, even mid-closing; listeners are detached first, as Fancybox's close() does. */
export const closeFancyboxInWindow = gallery => {
  if (!gallery || gallery.state === "destroy") return
  gallery.detachEvents()
  gallery.destroy()
}
